'use client'

import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { appPaths } from '@config'
import { Link } from '@assets-components'
import { Icon, Image, ImageData } from '@media-components'
import {
  ChipData,
  SectionWrapper,
  BreadcrumbsNav,
  BreadcrumbsNavPath,
  DocumentRendererData,
} from '@components/common'
import { SOCIAL_MEDIA, socialShare } from '@utils/shared'
import { CustomStyles, getStyles } from '@utils/styles'
import {
  ButtonsRenderer,
  ButtonsRendererData,
} from '../../../common/ButtonsRenderer'
import { ArticleReadAloud } from '../ArticleReadAloud'
import defaultStyles, { StylesClassNames } from './styles'

type BlogMetaInfoData = {
  authorName: string
  timeAboutBlogReading: string
  userImg?: ImageData
  authorDescription: string
  authorImage?: ImageData
  authorSlug?: string
  authorDesignation: string
}

export type InsightBannerData = {
  heading: string
  description: string
  publishedDate: string
  updatedOn: string
  chipData: ChipData[]
  blogMetaInfoData: BlogMetaInfoData
  featureImageData?: ImageData
  breadCrumbPath: BreadcrumbsNavPath[]
  documentData?: DocumentRendererData
  buttonsData?: ButtonsRendererData['buttonData']
}

export type InsightBannerProps = InsightBannerData & {
  csx?: CustomStyles<StylesClassNames>
  slug: string
}

const SHARE_OPTIONS = [
  { platform: SOCIAL_MEDIA.FACEBOOK, iconName: 'Facebook' as const },
  { platform: SOCIAL_MEDIA.INSTAGRAM, iconName: 'Instagram' as const },
  { platform: SOCIAL_MEDIA.LINKEDIN, iconName: 'LinkedIn' as const },
  { platform: SOCIAL_MEDIA.TWITTER, iconName: 'Twitter' as const },
]

export function InsightBanner({
  csx,
  publishedDate,
  description,
  heading,
  chipData,
  blogMetaInfoData,
  featureImageData,
  slug,
  breadCrumbPath,
  documentData,
  updatedOn,
  buttonsData,
}: InsightBannerProps) {
  const styles = getStyles(defaultStyles, csx)
  const { authorName, authorSlug, authorImage, timeAboutBlogReading } =
    blogMetaInfoData

  return (
    <SectionWrapper
      flexCol={{ xs: '20px', md: '32px' }}
      csx={{
        white: {
          backgroundColor: 'custom.sectionHighlight',
        },
      }}
    >
      <Box {...styles('outerWrapper')}>
        <Stack {...styles('contentColumn')}>
          <BreadcrumbsNav darkBg={false} />

          {chipData.length > 0 && (
            <Box {...styles('chipsWrapper')}>
              {chipData.map((chip) => (
                <Chip
                  key={chip.label}
                  label={
                    <Typography variant="body3.500">{chip.label}</Typography>
                  }
                  {...styles('chip')}
                  size="small"
                />
              ))}
            </Box>
          )}

          <ArticleReadAloud
            document={documentData?.document ?? []}
            articleId={slug}
          >
            <Typography
              variant="h4.600"
              color="text.primary"
              component="h1"
              {...styles('heading')}
            >
              {heading}
            </Typography>
          </ArticleReadAloud>

          {description && (
            <Typography
              variant="body1.500"
              color="text.primary"
              {...styles('description')}
            >
              {description}
            </Typography>
          )}

          <Box {...styles('authorMeta')}>
            {authorImage?.src && (
              <Image {...authorImage} {...styles('authorImage')} />
            )}

            {authorSlug ? (
              <Typography
                component={Link}
                href={`${appPaths.authors}/${authorSlug}`}
                variant="body2.600"
                color="text.primary"
                {...styles('authorName')}
              >
                {authorName}
              </Typography>
            ) : (
              <Typography variant="body2.600" color="text.primary">
                {authorName}
              </Typography>
            )}
            {timeAboutBlogReading ? (
              <>
                <Typography
                  variant="body3.700"
                  color="text.secondary"
                  {...styles('metaDivider')}
                >
                  &bull;
                </Typography>
                <Typography variant="body3.500" color="text.secondary">
                  {timeAboutBlogReading} min read
                </Typography>
              </>
            ) : null}

            <Box
              sx={{
                marginLeft: 'auto',
              }}
            >
              {publishedDate && (
                <>
                  <Typography
                    variant="body3.700"
                    color="text.secondary"
                    {...styles('metaDivider')}
                  >
                    Published on:
                  </Typography>
                  <Typography variant="body3.500" color="text.secondary">
                    {' '}
                    {publishedDate}
                  </Typography>
                </>
              )}

              {updatedOn && (
                <>
                  <Typography
                    variant="body3.500"
                    {...styles('metaDividerPipe')}
                  >
                    |
                  </Typography>
                  <Typography
                    variant="body3.700"
                    color="text.secondary"
                    {...styles('metaDivider')}
                  >
                    Updated on:
                  </Typography>
                  <Typography variant="body3.500" color="text.secondary">
                    {' '}
                    {updatedOn}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {buttonsData && buttonsData.length > 0 && (
            <ButtonsRenderer buttonData={buttonsData} />
          )}

          <Box {...styles('shareRow')}>
            <Typography variant="body2.600" color="text.primary">
              Share this blog:
            </Typography>
            <Box {...styles('shareIcons')}>
              {SHARE_OPTIONS.map(({ platform, iconName }) => (
                <Box
                  key={platform}
                  onClick={() =>
                    socialShare({
                      platform,
                      heading,
                      slug,
                    })
                  }
                  {...styles('shareIconButton')}
                >
                  <Icon iconName={iconName} sx={{ fontSize: '20px' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>

        {featureImageData?.src && (
          <Image
            preload
            fetchPriority="high"
            {...featureImageData}
            {...styles('featureImage')}
          />
        )}
      </Box>
    </SectionWrapper>
  )
}
